/**
 * FlyToCart custom element for animating product images to cart
 * This component creates a visual effect of a product "flying" to the cart when added
 */
class FlyToCart extends HTMLElement {
  source;
  destination;
  useSourceSize = false;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    if (!this.source || !this.destination) {
      this.remove();
      return;
    }

    const sourceRect = this.source.getBoundingClientRect();
    let destinationRect = this.destination.getBoundingClientRect();

    // Fallback if destination is hidden or zero-sized
    if (destinationRect.width === 0 || destinationRect.height === 0) {
      // Try to find another one or fallback to top right
      const fallback = document.querySelector('header-component .header-actions__cart-icon:not([hidden])')
        || document.querySelector('.header-actions__cart-icon:not([hidden])');
      
      if (fallback) {
        destinationRect = fallback.getBoundingClientRect();
      }
    }

    // Still fallback if still zero
    const endX = destinationRect.width > 0 ? destinationRect.left + destinationRect.width / 2 : window.innerWidth - 50;
    const endY = destinationRect.height > 0 ? destinationRect.top + destinationRect.height / 2 : 50;

    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };

    const endPoint = {
      x: endX,
      y: endY,
    };

    const dx = endPoint.x - startPoint.x;
    const dy = endPoint.y - startPoint.y;

    // Create wrappers to isolate X, Y and Scale/Opacity animations
    const wrapperX = document.createElement('div');
    const wrapperY = document.createElement('div');
    const wrapperScale = document.createElement('div');

    Object.assign(wrapperX.style, { 
      position: 'absolute', 
      inset: 0, 
      pointerEvents: 'none',
      willChange: 'transform'
    });
    Object.assign(wrapperY.style, { 
      position: 'absolute', 
      inset: 0, 
      pointerEvents: 'none',
      willChange: 'transform'
    });
    Object.assign(wrapperScale.style, {
      position: 'absolute',
      inset: 0,
      borderRadius: 'inherit',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundImage: this.style.backgroundImage || `url(${this.dataset.productVariantMedia})`,
      backgroundRepeat: 'no-repeat',
      backgroundColor: 'currentcolor',
      willChange: 'transform, opacity'
    });

    this.appendChild(wrapperX);
    wrapperX.appendChild(wrapperY);
    wrapperY.appendChild(wrapperScale);

    // Initial position
    this.style.position = 'fixed';
    this.style.left = `${startPoint.x}px`;
    this.style.top = `${startPoint.y}px`;
    this.style.transform = 'translate(-50%, -50%)';
    this.style.opacity = '1';
    this.style.zIndex = '100000';

    if (this.useSourceSize) {
      this.style.width = `${sourceRect.width}px`;
      this.style.height = `${sourceRect.height}px`;
    }

    const duration = 800;

    // Curved Path: X and Y have different easings
    // X moves more linearly or with a slight ease
    wrapperX.animate(
      [ { transform: 'translateX(0)' }, { transform: `translateX(${dx}px)` } ],
      { duration, easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)', fill: 'forwards' }
    );

    // Y moves with a significant arch (moves up first or with a delay)
    wrapperY.animate(
      [ { transform: 'translateY(0)' }, { transform: `translateY(${dy}px)` } ],
      { duration, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)', fill: 'forwards' }
    );

    wrapperScale.animate(
      [
        { transform: 'scale(1)', opacity: 1, borderRadius: '0%' },
        { transform: 'scale(0.3)', opacity: 0.5, borderRadius: '20%', offset: 0.8 },
        { transform: 'scale(0.1)', opacity: 0, borderRadius: '50%' }
      ],
      { duration, easing: 'ease-in-out', fill: 'forwards' }
    );

    // Clean up
    setTimeout(() => {
      this.remove();
    }, duration);
  }
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
